export type MachineType = 'p600z' | 'm500' | 'p625' | 'iwm'

export type WeldTorch = 'Lead' | 'Trail'

/**
 * One torch reading at a moment in time. Each TXT row contributes one Lead
 * sample and, for dual-torch machines (P600Z / P625), one Trail sample —
 * matching Data360 Live t-log ingestion. Volts may be null when that torch
 * has no electricals on the row; zone/pass still comes from Ld Pass / Tr Pass.
 */
export interface WeldSample {
  evtNo: number
  sampleTime: string
  startTime: string | null
  /**
   * Wall-clock UTC timestamp for this sample, derived from the
   * `Year/Month/Day/Hour/Minute/Second` columns at the end of every TXT row.
   * Stored as an ISO 8601 string (e.g. `2026-05-01T17:08:34.000Z`).
   * `null` when any of the six date/time columns is missing or non-numeric.
   */
  recordedAt: string | null
  duration: number | null
  tilt: number | null
  travelSpeed: number | null
  distance: number | null
  torch: WeldTorch
  /** Weld direction from Data360 bug_type or TXT filename (CW / CCW). */
  bugType?: 'CW' | 'CCW' | null
  passRaw: string | null
  volts: number | null
  amps: number | null
  wireIpm: number | null
  widthMil: number | null
  targetVlt: number | null
  csBias: number | null
  bpm: number | null
  totWireIn: number | null
  rprtHeat: number | null
  trueEnergy: number | null
  calcHeat: number | null
  /**
   * Trimmed `Weld #` value from this row's TXT columns. Used by
   * `filterSamplesByWeldNumber` to keep only samples matching the
   * FloorSheet Weld ID at upload time.
   */
  weldNumber?: string
  /**
   * Position of this sample's source TXT inside the user's multi-file
   * upload batch (0-based; `0` is the first file selected). Stamped by
   * `UploadWeldDataDialog.runUpload` AFTER parsing — the parser itself
   * never knows the batch shape, so it leaves this `null`. The server
   * persists this in `weld_telemetry_samples.source_file_index` so the
   * Pass Analytics table can render a divider between files. Optional so
   * historical test fixtures and any single-file callers stay valid.
   */
  sourceFileIndex?: number | null
}

export interface WeldTxtParseResult {
  samples: WeldSample[]
  distinctPassNames: string[]
  /** Unique non-empty `Weld #` values across all parsed rows in this file. */
  distinctWeldNumbers: string[]
  jobNumber: string
  /** First non-empty `Weld #` in the file (backward-compatible summary). */
  weldNumber: string
  totalRows: number
  error: string | null
}

export type WeldNumberFilterResult = {
  kept: WeldSample[]
  excludedCount: number
  distinctInFile: string[]
}

const EMPTY_RESULT = (error: string): WeldTxtParseResult => ({
  samples: [],
  distinctPassNames: [],
  distinctWeldNumbers: [],
  jobNumber: '',
  weldNumber: '',
  totalRows: 0,
  error
})

/**
 * Compare two Weld # tokens for upload filtering. Trims whitespace and
 * ignores letter case only — no zero-padding or numeric normalization
 * (e.g. `1` does not match `Weld-0001`).
 */
export function weldNumbersMatch(a: string, b: string): boolean {
  const left = a.trim()
  const right = b.trim()
  if (!left || !right) return false
  return left.toLowerCase() === right.toLowerCase()
}

/**
 * Keep only samples whose per-row `weldNumber` matches `expectedWeldId`
 * via {@link weldNumbersMatch}. Samples with an empty/missing weldNumber are
 * excluded when a non-empty expected id is provided.
 */
export function filterSamplesByWeldNumber(
  samples: WeldSample[],
  expectedWeldId: string
): WeldNumberFilterResult {
  const expected = expectedWeldId.trim()
  const distinctSet = new Set<string>()
  for (const sample of samples) {
    const wn = (sample.weldNumber ?? '').trim()
    if (wn) distinctSet.add(wn)
  }
  const distinctInFile = Array.from(distinctSet).sort()

  if (!expected) {
    return { kept: samples, excludedCount: 0, distinctInFile }
  }

  const kept: WeldSample[] = []
  let excludedCount = 0
  for (const sample of samples) {
    const wn = (sample.weldNumber ?? '').trim()
    if (weldNumbersMatch(wn, expected)) {
      kept.push(sample)
    } else {
      excludedCount += 1
    }
  }
  return { kept, excludedCount, distinctInFile }
}

/** Machine types that only emit Lead samples (IWM / M500), like Data360 Live. */
function isLeadOnlyMachineType(machineType: MachineType): boolean {
  return machineType === 'm500' || machineType === 'iwm'
}

/**
 * Required headers the parser refuses to parse a file without. Both Lead and
 * Trail pass + volts headers are required for dual-torch TXT exports.
 */
const REQUIRED_HEADER_GROUPS: { label: string; aliases: string[] }[] = [
  { label: 'Evt #', aliases: ['evt', 'evt_no', 'evt_number'] },
  { label: 'Time', aliases: ['time'] },
  { label: 'Start Time', aliases: ['start_time', 's_time'] },
  { label: 'Job #', aliases: ['job', 'job_no', 'job_number'] },
  { label: 'Weld #', aliases: ['weld', 'weld_no', 'weld_number'] },
  { label: 'Lead pass (Ld Pass)', aliases: ['ld_pass', 'lead_pass'] },
  { label: 'Trail pass (Tr Pass)', aliases: ['tr_pass', 'trail_pass'] },
  { label: 'Lead volts (Ld Volts)', aliases: ['ld_volts'] },
  { label: 'Trail volts (Tr Volts)', aliases: ['tr_volts'] },
  { label: 'Year', aliases: ['year'] },
  { label: 'Month', aliases: ['month'] },
  { label: 'Day', aliases: ['day'] },
  { label: 'Hour', aliases: ['hour'] },
  { label: 'Minute', aliases: ['minute'] },
  { label: 'Second', aliases: ['second'] }
]

export function parseWeldTxt(text: string, machineType: MachineType): WeldTxtParseResult {
  if (text.trim().length === 0) {
    return EMPTY_RESULT(
      'File is empty or does not contain data rows. Expected a tab-separated header row followed by weld records.'
    )
  }

  const rawLines = text.split(/\r?\n/).map((line) => line.replace(/\r$/, ''))
  const headerLine = rawLines[0]

  if (!headerLine.includes('\t')) {
    return EMPTY_RESULT(
      'This does not look like a welding-machine TXT export — the first line is not tab-separated. ' +
        'Please upload the original TXT file produced by the machine.'
    )
  }

  const headerColumns = headerLine.split('\t').map(normalizeHeader)
  if (headerColumns.every((h) => h === '')) {
    return EMPTY_RESULT(
      'No column headers were detected in the first line of the file. Please upload the original welding-machine TXT export.'
    )
  }

  const dataLines = rawLines.slice(1).filter((line) => line.trim().length > 0)
  if (dataLines.length === 0) {
    return EMPTY_RESULT(
      'File contains a header row but no data rows. Please upload a TXT with at least one welding record.'
    )
  }

  const headerSet = new Set(headerColumns.filter((h) => h.length > 0))
  const missingHeaderLabels = REQUIRED_HEADER_GROUPS
    .filter((group) => !group.aliases.some((alias) => headerSet.has(alias)))
    .map((group) => group.label)

  if (missingHeaderLabels.length > 0) {
    return EMPTY_RESULT(
      `The selected file is missing required column${missingHeaderLabels.length > 1 ? 's' : ''}: ` +
        `${missingHeaderLabels.join(', ')}. ` +
        'Please make sure you upload the original welding-machine TXT export with all standard headers.'
    )
  }

  const samples: WeldSample[] = []
  let jobNumber = ''
  let weldNumber = ''
  const passNameSet = new Set<string>()
  const weldNumberSet = new Set<string>()
  const leadOnly = isLeadOnlyMachineType(machineType)

  for (const line of dataLines) {
    const cols = line.split('\t')
    const row: Record<string, string> = {}
    headerColumns.forEach((key, idx) => {
      if (key) row[key] = cols[idx] ?? ''
    })

    if (!jobNumber) {
      jobNumber = firstNonEmpty(row, ['job', 'job_no', 'job_number'])
    }
    const rowWeldNumber = firstNonEmpty(row, ['weld', 'weld_no', 'weld_number'])
    if (!weldNumber && rowWeldNumber) {
      weldNumber = rowWeldNumber
    }
    if (rowWeldNumber) {
      weldNumberSet.add(rowWeldNumber)
    }

    const rowSamples: WeldSample[] = [leadSample(row)]
    if (!leadOnly) {
      rowSamples.push(trailSample(row))
    }

    for (const sample of rowSamples) {
      if (rowWeldNumber) {
        sample.weldNumber = rowWeldNumber
      }
      if (sample.passRaw) passNameSet.add(sample.passRaw)
      samples.push(sample)
    }
  }

  return {
    samples,
    distinctPassNames: Array.from(passNameSet).sort(),
    distinctWeldNumbers: Array.from(weldNumberSet).sort(),
    jobNumber,
    weldNumber,
    totalRows: samples.length,
    error: null
  }
}

function leadSample(row: Record<string, string>): WeldSample {
  return {
    ...rowLevelFields(row),
    torch: 'Lead',
    passRaw: nullableString(firstNonEmpty(row, ['ld_pass', 'lead_pass'])),
    volts: toNumber(firstNonEmpty(row, ['ld_volts'])),
    amps: toNumber(firstNonEmpty(row, ['ld_amps'])),
    wireIpm: toNumber(firstNonEmpty(row, ['ld_wire_ipm'])),
    widthMil: toNumber(firstNonEmpty(row, ['ld_width_mil'])),
    targetVlt: toNumber(firstNonEmpty(row, ['ld_tgt_vlt', 'ld_target_vlt'])),
    csBias: toNumber(firstNonEmpty(row, ['ld_cs_bias'])),
    bpm: toNumber(firstNonEmpty(row, ['ld_bpm'])),
    totWireIn: toNumber(firstNonEmpty(row, ['ld_tot_wire_in'])),
    rprtHeat: toNumber(firstNonEmpty(row, ['ld_rprt_heat'])),
    trueEnergy: toNumber(firstNonEmpty(row, ['ld_true_energy'])),
    calcHeat: toNumber(firstNonEmpty(row, ['ld_calc_heat_kj_i', 'ld_calc_heat'])),
    sourceFileIndex: null
  }
}

function trailSample(row: Record<string, string>): WeldSample {
  return {
    ...rowLevelFields(row),
    torch: 'Trail',
    passRaw: nullableString(firstNonEmpty(row, ['tr_pass', 'trail_pass'])),
    volts: toNumber(firstNonEmpty(row, ['tr_volts'])),
    amps: toNumber(firstNonEmpty(row, ['tr_amps'])),
    wireIpm: toNumber(firstNonEmpty(row, ['tr_wire_ipm'])),
    widthMil: toNumber(firstNonEmpty(row, ['tr_width_mil'])),
    targetVlt: toNumber(firstNonEmpty(row, ['tr_tgt_vlt', 'tr_target_vlt'])),
    csBias: toNumber(firstNonEmpty(row, ['tr_cs_bias'])),
    bpm: toNumber(firstNonEmpty(row, ['tr_bpm'])),
    totWireIn: toNumber(firstNonEmpty(row, ['tr_tot_wire_in'])),
    rprtHeat: toNumber(firstNonEmpty(row, ['tr_rprt_heat'])),
    trueEnergy: toNumber(firstNonEmpty(row, ['tr_true_energy'])),
    calcHeat: toNumber(firstNonEmpty(row, ['tr_calc_kj_i', 'tr_calc_heat'])),
    sourceFileIndex: null
  }
}

function rowLevelFields(row: Record<string, string>): Pick<
  WeldSample,
  | 'evtNo'
  | 'sampleTime'
  | 'startTime'
  | 'recordedAt'
  | 'duration'
  | 'tilt'
  | 'travelSpeed'
  | 'distance'
> {
  return {
    evtNo: toNumber(firstNonEmpty(row, ['evt', 'evt_no', 'evt_number'])) ?? 0,
    sampleTime: firstNonEmpty(row, ['time']),
    startTime: nullableString(firstNonEmpty(row, ['start_time', 's_time'])),
    recordedAt: buildRecordedAt(row),
    duration: toNumber(firstNonEmpty(row, ['duration'])),
    tilt: toNumber(firstNonEmpty(row, ['tilt'])),
    travelSpeed: toNumber(firstNonEmpty(row, ['tvl_ipm'])),
    distance: toNumber(firstNonEmpty(row, ['dist']))
  }
}

/**
 * Combine the six wall-clock columns into a UTC ISO timestamp. Returns
 * `null` if any field is missing or non-numeric so the database column
 * stays `NULL` rather than getting a bogus `1970-01-01` value.
 */
function buildRecordedAt(row: Record<string, string>): string | null {
  const year = toNumber(firstNonEmpty(row, ['year']))
  const month = toNumber(firstNonEmpty(row, ['month']))
  const day = toNumber(firstNonEmpty(row, ['day']))
  const hour = toNumber(firstNonEmpty(row, ['hour']))
  const minute = toNumber(firstNonEmpty(row, ['minute']))
  const second = toNumber(firstNonEmpty(row, ['second']))
  if (
    year === null ||
    month === null ||
    day === null ||
    hour === null ||
    minute === null ||
    second === null
  ) {
    return null
  }
  // Months are 0-indexed in `Date.UTC`. Whole-second precision is enough for
  // the welding TXT export — the milliseconds component is always zero.
  const ms = Date.UTC(year, month - 1, day, hour, minute, second)
  if (!Number.isFinite(ms)) return null
  return new Date(ms).toISOString()
}

function firstNonEmpty(row: Record<string, string>, aliases: string[]): string {
  for (const key of aliases) {
    const value = row[key]
    if (value !== undefined && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

function nullableString(value: string): string | null {
  return value === '' ? null : value
}

function toNumber(value: string): number | null {
  if (value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function normalizeHeader(value: string): string {
  return String(value || '')
    .replace(/^\uFEFF/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .trim()
}
